import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IconSvg from 'src/components/common/ui/IconSvg';
import { useHttp, useNotification } from 'src/hooks';

const OneTimePasswordForm: React.FC = () => {
  const { sendRequest, isLoading, error } = useHttp();
  const notify = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState<string[]>(Array(5).fill(''));
  const email = location.state?.email || '';

  const isOtpComplete = otp.every(digit => digit !== '');

  const handleChange = (value: string, index: number) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{5}$/.test(pasteData)) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);

      const lastInput = document.getElementById(`otp-${otp.length - 1}`);
      lastInput?.focus();
    }
  };

  const handleVerify = () => {
    if (!email) {
      notify('Please enter an email first.', 'error');
      return;
    }
    const otpString = otp.join('');

    sendRequest(
      {
        method: 'POST',
        url: `auth/validate-otp`,
        payload: { email: email, otp: parseInt(otpString) },
      },
      data => {
        notify(data.message, 'success');
        navigate('/new-password', { state: { email: email, otp: otp } });
      },
    );
  };

  const handleResend = () => {
    if (!email) {
      notify('Please enter an email first.', 'error');
      return;
    }
    sendRequest(
      {
        method: 'POST',
        url: `auth/generate-otp`,
        payload: { email: email },
      },
      () => {
        notify('Email resent! Please check your inbox.', 'success');
      },
    );
  };

  const handleBack = () => {
    navigate('/forgot-password');
  };

  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);

  return (
    <div className="lg:w-1/2 w-full lg:p-8 flex flex-col items-start">
      <IconSvg icon="appLogo" className="cursor-pointer lg:w-auto w-24" onClick={() => navigate('/')} alt="Logo" />

      <p className="lg:text-[36px] text-[28px] font-semibold text-black font-inter mt-8 lg:mb-6 mb-4">
        One-Time Password
      </p>
      <p className="text-[#4A4D4F] lg:text-xl text-base font-normal font-inter mb-6">
        2FA’s a hassle, we know—but it’s for your safety! Check your email for the code.
      </p>
      <form className="w-full">
        {/* OTP Input Fields */}
        <div className="mb-6 flex items-center justify-between gap-4 w-full">
          {' '}
          {/* Increased gap to 16px */}
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={digit}
              maxLength={1}
              onChange={e => handleChange(e.target.value, index)}
              onKeyDown={e => handleKeyDown(e, index)}
              onPaste={handlePaste}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
              }}
              style={{
                boxSizing: 'border-box',
                width: '98.8px',
                height: '98px',
                background: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.32)',
                borderRadius: '12px',
                flex: 'none',
                order: 3,
                flexGrow: 1,
                textAlign: 'center',
                fontSize: '24px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                outline: 'none',
              }}
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          type="button"
          disabled={!isOtpComplete || !email}
          className={`w-full py-4 rounded-lg lg:text-lg text-sm font-semibold ${isOtpComplete && email
            ? 'bg-blue-500 text-white'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          onClick={handleVerify}
        >
          Verify
        </button>
      </form>

      {/* Resend or Back Link */}
      <div className="mt-4 flex items-center justify-center text-black w-full text-center lg:text-lg text-sm font-semibold">
        {email ? (
          <p
            onClick={isLoading ? undefined : handleResend}
            className={`cursor-pointer underline underline-offset-4 ${isLoading ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-[#6277FF]'
              }`}
          >
            Resend
          </p>
        ) : (
          <p
            onClick={handleBack}
            className="cursor-pointer underline underline-offset-4 text-[#6277FF]"
          >
            Back
          </p>
        )}
      </div>
    </div>
  );
};

export default OneTimePasswordForm;
